/**
 * The public website around the game: canonical URLs, sport landing pages, analytics IDs and the
 * analytics consent rule. Shared by the app, its prerendered pages and the Worker's content pages.
 */
import { GAME_DOMAIN, GAME_NAME, MAX_TOTAL, SPORTS } from './config';
import type { Sport } from './types';

export const SITE_URL = `https://${GAME_DOMAIN}`;
export const SITE_TAGLINE = 'The daily where + when sports quiz';
export const SITE_DESCRIPTION = `${GAME_NAME} is a free daily sports geography game: five famous sports moments a day. Pin where each one happened on a 3D globe, guess the year, then read the story and compare scores with friends.`;
/** 1200×630 social preview image. */
export const SITE_IMAGE = `${SITE_URL}/og.png`;
export const SITE_LOGO = `${SITE_URL}/icons/icon-512.png`;
/** Public contact address for players, press, directories and data requests. */
export const CONTACT_EMAIL = 'hello.geosportle@gmail.com';

/**
 * Our listing on Listdle (daily-games directory, approved 2026-10-07). The footers link to it
 * with its "Rate us" badge, served from /badges/listdle.svg so no third party sees the visit.
 */
export const LISTDLE_URL = 'https://listdle.com/games/geosportle';

/**
 * Google Analytics 4 web stream for geosportle.app (public by design: it ships in the page).
 * Property "Geosportle" (557379746) in the hello.pixelpuff@gmail.com account with the other projects.
 */
export const GA_MEASUREMENT_ID = 'G-0LDWYP07Q2';
/** IndexNow key (public by design: it is served at /<key>.txt). */
export const INDEXNOW_KEY = 'be62bbe204f99991394b9a29aa41e2d8';

/** localStorage key holding the visitor's analytics choice: "granted" or "denied". */
export const ANALYTICS_CONSENT_KEY = 'geosport:analytics-consent';

/**
 * Time zones of the EEA, the UK and Switzerland, where analytics cookies need opt-in consent
 * (GDPR, UK GDPR + PECR, FADP). Includes EU regions outside Europe (Canaries, Madeira, Azores,
 * Ceuta, Cyprus, French overseas departments).
 */
export const CONSENT_TIME_ZONES =
  /^(?:Europe\/.+|Atlantic\/(?:Reykjavik|Canary|Madeira|Azores|Faroe)|Arctic\/Longyearbyen|Asia\/(?:Nicosia|Famagusta)|Africa\/Ceuta|America\/(?:Guadeloupe|Martinique|Cayenne)|Indian\/(?:Reunion|Mayotte))$/;

/**
 * DECISION: the browser's time zone decides whether analytics needs opt-in first. It needs no
 * network lookup and errs towards asking: a missing or neutral zone (UTC, Etc/*, which privacy
 * hardened browsers report) counts as "ask".
 */
export function analyticsConsentNeeded(timeZone: string | null | undefined): boolean {
  if (!timeZone || timeZone === 'UTC' || timeZone.startsWith('Etc/')) return true;
  return CONSENT_TIME_ZONES.test(timeZone);
}

/** Landing page per sport category: URL slug, display name and emoji. */
export const SPORT_PAGES: Record<Sport, { slug: string; name: string; emoji: string }> = {
  football: { slug: 'football', name: 'Football', emoji: '⚽' },
  cricket: { slug: 'cricket', name: 'Cricket', emoji: '🏏' },
  f1: { slug: 'formula-1', name: 'Formula 1', emoji: '🏎️' },
  tennis: { slug: 'tennis', name: 'Tennis', emoji: '🎾' },
  rugby: { slug: 'rugby', name: 'Rugby', emoji: '🏉' },
  golf: { slug: 'golf', name: 'Golf', emoji: '⛳' },
  athletics: { slug: 'athletics', name: 'Athletics', emoji: '🏃' },
  other: { slug: 'more-sports', name: 'More sports', emoji: '🏅' },
};

export function sportFromSlug(slug: string): Sport | null {
  return SPORTS.find((s) => SPORT_PAGES[s].slug === slug) ?? null;
}

/** Indexable pages of the app itself (prerendered at build time; the rest are noindex). */
export const APP_PAGES = [
  '/',
  '/how-to-play',
  '/leaderboard',
  '/practice',
  '/groups',
  '/privacy',
  '/terms',
] as const;

export const sportPath = (sport: Sport): string => `/sports/${SPORT_PAGES[sport].slug}`;
export const puzzlePath = (puzzleNo: number): string => `/puzzles/${puzzleNo}`;
/** A themed special's landing page, and where its round is played in the app. */
export const specialPath = (slug: string): string => `/specials/${slug}`;
export const specialPlayPath = (slug: string): string => `/play/special/${slug}`;
/** Lowercase words joined by hyphens, 3–60 characters. */
export const SPECIAL_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export interface Challenge {
  puzzleNo: number;
  score: number;
}

/** A "beat my score" link for one puzzle, e.g. /challenge/12/873. */
export const challengePath = (puzzleNo: number, score: number): string =>
  `/challenge/${puzzleNo}/${score}`;

export function parseChallenge(path: string): Challenge | null {
  const m = /^\/challenge\/([1-9]\d{0,5})\/(\d{1,4})$/.exec(path);
  if (!m) return null;
  const score = Number(m[2]);
  return score <= MAX_TOTAL ? { puzzleNo: Number(m[1]), score } : null;
}

/** Text for the "Challenge a friend" share: the score to beat and the link. */
export function challengeShareText(puzzleNo: number, score: number): string {
  return `I scored ${score}/${MAX_TOTAL} on ${GAME_NAME} #${puzzleNo} 🌍 Think you know your sport? Beat me:\n${SITE_URL}${challengePath(puzzleNo, score)}`;
}
